import {useCallback, useMemo} from 'react';
import {
  DEAL_STATUSES,
  Offer,
  OFFER_CATEGORIES,
  OFFER_CONFIRMATION_TYPES,
  UNIT_PRICE_TYPES,
} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useCreateBulkDealMutation} from './useCreateBulkDealMutation';
import {useFetchDeals} from './useFetchDeals';
import {useFetchOffers} from './useFetchOffers';
import {useGuestGroup} from './useGuestGroup';
import {useMutateDeals} from './useMutateDeals';
import {useSummary} from 'context/summary';

const FIXED_PET_FEE_QUANTITY = 1;
const EDITABLE_PET_FEE_DEAL_STATUSES = [
  DEAL_STATUSES.pre_requested,
  DEAL_STATUSES.approved,
];

const isPetFeeOffer = (offer?: Offer) => offer?.category === OFFER_CATEGORIES.petFee;

const getPetFeeQuantity = (offer: Offer, numberOfPets: number) => {
  const unitType = offer.items[0]?.unit_type;
  const isPerPetOrUnit =
    unitType === UNIT_PRICE_TYPES.pet || unitType === UNIT_PRICE_TYPES.unit;

  return isPerPetOrUnit ? numberOfPets : FIXED_PET_FEE_QUANTITY;
};

function usePetFeeDeal() {
  const {data: reservation} = useReservation();
  const {guestGroup} = useGuestGroup();
  const {data: paginatedPetFeeOffers, isLoading: isPetFeeOffersLoading} = useFetchOffers({
    housingId: reservation?.housing_id,
    category: OFFER_CATEGORIES.petFee,
  });
  const {data: paginatedDealsList, isLoading: isPetFeeDealsLoading} = useFetchDeals({
    reservationId: reservation?.id,
  });
  const {data: summary} = useSummary();
  const {mutateAsync: createDeal, isPending: isCreatingPetFeeDeal} =
    useCreateBulkDealMutation();
  const {deleteDealAsyncMutation, isDeletingDeal: isDeletingPetFeeDeal} =
    useMutateDeals();
  
  const petFeeOffer = useMemo(() => {
    return (
      summary?.upselling?.active && paginatedPetFeeOffers?.results.find(isPetFeeOffer)
    );
  }, [summary?.upselling?.active, paginatedPetFeeOffers?.results]);

  const editablePetFeeDeal = useMemo(() => {
    return paginatedDealsList?.results.find(
      deal =>
        deal.offer.category === OFFER_CATEGORIES.petFee &&
        EDITABLE_PET_FEE_DEAL_STATUSES.includes(deal.status),
    );
  }, [paginatedDealsList?.results]);

  const createPetFeeDeal = useCallback(
    async (numberOfPets?: number) => {
      if (!reservation || !petFeeOffer || isCreatingPetFeeDeal || isDeletingPetFeeDeal) {
        return;
      }

      try {
        if (editablePetFeeDeal) {
          await deleteDealAsyncMutation(editablePetFeeDeal.id);
        }

        if (!numberOfPets || numberOfPets < 1) {
          return;
        }

        const requestedItem = petFeeOffer.items[0];
        if (!requestedItem) {
          return;
        }

        const coreGuest = guestGroup?.members.find(
          ({id}) => id === guestGroup?.leader_id,
        );
        const quantity = getPetFeeQuantity(petFeeOffer, numberOfPets);
        const dealPayload = {
          core_housing_id: reservation.housing_id,
          core_reservation_id: reservation.id,
          core_guest_name: coreGuest?.full_name,
          core_housing_name: reservation.housing_name,
          core_room_numbers: reservation.rooms.map(room => room.number),
          guest_email: coreGuest?.email || reservation.default_invite_email,
          core_guest_id: coreGuest?.id,
          offer_id: petFeeOffer.id,
          quantity: FIXED_PET_FEE_QUANTITY,
          payments_version: 'V2' as const,
          number_of_people: quantity,
          requested_items: [
            {
              item: requestedItem.id,
              quantity,
            },
          ],
          status:
            petFeeOffer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
              ? DEAL_STATUSES.approved
              : DEAL_STATUSES.pre_requested,
        };

        await createDeal({
          deals: [dealPayload],
        });
      } catch {
        return;
      }
    },
    [
      createDeal,
      deleteDealAsyncMutation,
      editablePetFeeDeal,
      guestGroup,
      isCreatingPetFeeDeal,
      isDeletingPetFeeDeal,
      petFeeOffer,
      reservation,
    ],
  );

  return {
    createPetFeeDeal,
    hasPetFeeOffer: Boolean(petFeeOffer),
    isLoadingPetFeeOffer: isPetFeeOffersLoading || isPetFeeDealsLoading,
    isCreatingPetFeeDeal: isCreatingPetFeeDeal || isDeletingPetFeeDeal,
  };
}

export {usePetFeeDeal};
